/**
 * Processing-engine framework — shared config model (client ⇄ server contract).
 *
 * When a user connects a Google account, Lapo watches their Gmail + Calendar and runs each incoming
 * item (a new email, a new/changed calendar event) through an ORDERED pipeline of "engines". Unlike
 * the single-winner email `ProcessorRegistry` on the server (highest match() score runs, exactly one
 * processor), every ENABLED engine here runs in the user's chosen ORDER, each able to hand context to
 * the next and to halt the chain. This module owns only the user-facing CONFIG — which engines are
 * enabled and in what order — persisted server-side (one doc per user) so it follows across devices
 * and is editable in Settings → Automations. The engine implementations live on the server.
 */

/**
 * Which normalized event kinds an engine acts on. Mirrors the server-side engine's `appliesTo`.
 * `both` covers email + calendar (the Google-sourced pair); `todoist` engines run only on Todoist
 * webhook events, which flow through a different, cursor-less ingest path.
 */
export type EngineAppliesTo = 'email' | 'calendar' | 'todoist' | 'both';

/**
 * One user-editable setting an engine exposes, so Settings → Automations can render a form field for it
 * without knowing the engine. `kind` picks the control; `select` uses `options`. The value is written
 * back into that engine's opaque {@link EngineConfigEntry.settings} blob under `key`.
 */
export interface EngineSettingField {
  key: string;
  label: string;
  kind: 'text' | 'select';
  hint?: string;
  placeholder?: string;
  /** Choices for `kind: 'select'`. */
  options?: Array<{ value: string; label: string }>;
}

/** A catalog entry describing one available built-in engine (server-provided, read-only to the UI). */
export interface EngineCatalogEntry {
  id: string;
  title: string;
  description: string;
  appliesTo: EngineAppliesTo;
  /** The engine's user-editable settings schema (empty/absent when it has none). */
  settings?: EngineSettingField[];
}

/** One engine's placement in a user's pipeline: enabled?, position, and its opaque settings blob. */
export interface EngineConfigEntry {
  /** matches an EngineCatalogEntry.id */
  engineId: string;
  enabled: boolean;
  /** ascending run order; the pipeline sorts by this before executing */
  order: number;
  /** per-user, per-engine settings passed to the engine at run time (shape owned by each engine) */
  settings?: Record<string, unknown>;
}

/** A user's full pipeline configuration: their ordered list of engine placements. */
export interface EngineConfig {
  entries: EngineConfigEntry[];
  /**
   * Extra Gmail labels whose messages are ignored by the email pipeline, ON TOP of the built-in
   * defaults (spam, trash, and anything auto-archived out of the inbox — enforced by the inbox-only
   * gate). A message added to the inbox that ALSO carries any of these labels (e.g. a category tab like
   * `CATEGORY_PROMOTIONS`, or a user label id) is skipped before it's fetched or triaged. Case-sensitive
   * Gmail label ids/names.
   */
  ignoredLabels?: string[];
}

/** Cap on user-supplied ignored labels — a generous ceiling that stops a pathological PUT from ballooning. */
const MAX_IGNORED_LABELS = 50;

/** Coerce an untrusted `ignoredLabels` value into a clean, trimmed, de-duplicated, capped string list. */
function coerceIgnoredLabels(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of input) {
    if (typeof v !== 'string') continue;
    const t = v.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
    if (out.length >= MAX_IGNORED_LABELS) break;
  }
  return out;
}

/**
 * Default config: an empty pipeline. Built-in engines ship DISABLED — the user opts each one in from
 * Settings → Automations. This is deliberate cost/safety hygiene: an AI-triage or task-capture engine
 * should never run (spend tokens / create Todoist tasks) until the user has explicitly turned it on.
 */
export function defaultEngineConfig(): EngineConfig {
  return { entries: [] };
}

function coerceEntry(input: unknown, index: number): EngineConfigEntry | null {
  if (!input || typeof input !== 'object') return null;
  const o = input as Record<string, unknown>;
  const engineId = typeof o.engineId === 'string' ? o.engineId.trim() : '';
  if (!engineId) return null;
  return {
    engineId,
    enabled: typeof o.enabled === 'boolean' ? o.enabled : false,
    // Fall back to array position so a client that omits `order` still yields a stable sequence.
    order: typeof o.order === 'number' && Number.isFinite(o.order) ? o.order : index,
    settings: o.settings && typeof o.settings === 'object' ? (o.settings as Record<string, unknown>) : undefined,
  };
}

/**
 * Coerce untrusted input (a PUT body) into a valid EngineConfig: drop malformed entries, dedupe by
 * engineId (first wins), and re-number `order` densely from 0 in the given sequence so the persisted
 * config is always a clean, gap-free ordering the UI can render and the server can iterate.
 */
export function coerceEngineConfig(input: unknown): EngineConfig {
  if (!input || typeof input !== 'object') return defaultEngineConfig();
  const raw = (input as Record<string, unknown>).entries;
  const list = Array.isArray(raw) ? raw : [];
  const seen = new Set<string>();
  const entries: EngineConfigEntry[] = [];
  for (const [i, item] of list.entries()) {
    const entry = coerceEntry(item, i);
    if (!entry || seen.has(entry.engineId)) continue;
    seen.add(entry.engineId);
    entries.push(entry);
  }
  // Sort by the requested order, then renumber densely so `order` is canonical.
  entries.sort((a, b) => a.order - b.order);
  entries.forEach((e, i) => (e.order = i));
  const ignoredLabels = coerceIgnoredLabels((input as Record<string, unknown>).ignoredLabels);
  return ignoredLabels.length ? { entries, ignoredLabels } : { entries };
}
